import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from ".";
import axios from "axios";
import { axiosGetWith429Retry } from "../../services/httpRetry";
import {
  createRequestDedupe,
  SCREEN_ENTRY_THROTTLE_MS,
} from "./requestDedupe";

// Keyed on the filters, so switching category or searching is never mistaken
// for a repeat of the previous question. getMoreChallenges is deliberately NOT
// throttled — it is pagination, and a suppressed page never arrives.
// In-flight dedup ONLY — no time throttle. The Sfide page clears the list and
// then refetches on every view entry:
//
//     dispatch(clearChallengeList());
//     dispatch(getChallenges(buildParams(1)));
//
// A throttle turns the second line into a no-op, and because the first line has
// already emptied the store the member is left looking at "Nessuna sfida
// trovata" — intermittently, depending on how quickly they re-enter the page.
// Joining a request that is already running is still safe and still collapses
// the burst; refusing to ask at all is not, wherever a caller clears first.
const challengesDedupe = createRequestDedupe({
  throttleMs: 0,
  keyOf: (arg) => JSON.stringify(arg ?? {}),
});
const categoriesDedupe = createRequestDedupe({
  throttleMs: SCREEN_ENTRY_THROTTLE_MS,
});

// ============ GET CHALLENGES ============
/**
 * One builder for the query, used by the request AND by the freshness check.
 *
 * They have to agree exactly: if the check hashed the raw argument while the
 * request hashed the built params, the two keys would never match and the
 * throttle would quietly do nothing.
 */
const challengeListParams = ({
  subscription = "all",
  category = null,
  term = null,
  liked = null,
  concluded = null,
  page = 1,
  limit = 15,
} = {}) => {
  const params = { scope: "enabled", subscription, page, limit };

  if (category !== null) params.category = category;
  if (term && term.trim() !== "") params.term = term.trim();
  if (liked !== null) params.liked = liked;
  if (concluded !== null) params.concluded = concluded;

  return params;
};

export const getChallenges = createAsyncThunk(
  "challenges/getChallenges",
  async (arg) => {
    const params = challengeListParams(arg);

    return challengesDedupe.run(params, async () => {
      const response = await axiosGetWith429Retry(
        `${baseUrl}/api/v1/subscriber-challenges/list`,
        { headers: headersDefault(), params }
      );

      return response.data;
    });
  },
  { condition: (arg) => !challengesDedupe.isFresh(challengeListParams(arg)) }
);

// ============ GET MORE CHALLENGES (Pagination) ============
export const getMoreChallenges = createAsyncThunk(
  "challenges/getMoreChallenges",
  async ({ subscription, category, term, liked, concluded, page, limit }) => {
    const params = { scope: "enabled", subscription, page, limit };

    if (category !== null) params.category = category;
    if (term && term.trim() !== "") params.term = term.trim();
    if (liked !== null) params.liked = liked;
    if (concluded !== null) params.concluded = concluded;

    const response = await axios.get(
      `${baseUrl}/api/v1/subscriber-challenges/list`,
      { headers: headersDefault(), params }
    );

    return response.data;
  }
);

// ============ GET CATEGORIES ============
export const getChallengeCategories = createAsyncThunk(
  "challenges/getCategories",
  async () => {
    return categoriesDedupe.run(undefined, async () => {
      const response = await axiosGetWith429Retry(
        `${baseUrl}/api/v1/subscriber-challenges/categories/list`,
        { headers: headersDefault() }
      );
      return response.data;
    });
  },
  { condition: () => !categoriesDedupe.isFresh() }
);

// ============ GET SETTINGS (Come funziona) ============
export const getChallengeSettings = createAsyncThunk(
  "challenges/getSettings",
  async () => {
    const response = await axios.get(
      `${baseUrl}/api/v1/subscriber-challenges/settings`,
      { headers: headersDefault() }
    );
    return response.data;
  }
);

// ============ SUBSCRIBE ============
export const subscribeToChallenge = createAsyncThunk(
  "challenges/subscribe",
  async ({ challengeId, startAt }) => {
    const response = await axios.post(
      `${baseUrl}/api/v1/subscriber-challenges/participations/${challengeId}`,
      { start_at: startAt },
      { headers: headersDefault() }
    );
    return { ...response.data, challengeId };
  }
);

// ============ UNSUBSCRIBE (Leave Challenge) ============
export const unsubscribeFromChallenge = createAsyncThunk(
  "challenges/unsubscribe",
  async ({ challengeId }) => {
    const response = await axios.delete(
      `${baseUrl}/api/v1/subscriber-challenges/participations/${challengeId}`,
      { headers: headersDefault() }
    );
    return { ...response.data, challengeId };
  }
);

// ============ RECORD ACTIVITY ============
export const recordChallengeActivity = createAsyncThunk(
  "challenges/recordActivity",
  async ({ challengeId, activityDate }, { rejectWithValue }) => {
    try {
      // Confirm directly against the challenge — no push_notification_action.
      // Points come from the challenge's reward_points server-side.
      const response = await axios.post(
        `${baseUrl}/api/v1/subscriber-challenges/activities/${challengeId}`,
        // When the confirm comes from a challenge notification deep-link, pass
        // the day the notification referred to so the backend can reject a
        // stale/old notification ("Non è più possibile confermare questa
        // attività."). Omitted for the in-app card flow.
        activityDate ? { activity_date: activityDate } : {},
        { headers: headersDefault() },
      );
      return { ...response.data, challengeId };
    } catch (error) {
      const backendMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Errore durante la conferma attività";

      return rejectWithValue(backendMessage);
    }
  }
);

// ============ PIN (LIKE) ============
export const pinChallenge = createAsyncThunk(
  "challenges/pin",
  async ({ challengeId }) => {
    const response = await axios.post(
      `${baseUrl}/api/v1/reactions/react`,
      { id: challengeId, type: "club/models/subscriber-challenge", reaction: "like" },
      { headers: headersDefault() }
    );
    return { ...response.data, challengeId };
  }
);

// ============ UNPIN (UNLIKE) ============
export const unpinChallenge = createAsyncThunk(
  "challenges/unpin",
  async ({ challengeId }) => {
    const response = await axios.delete(
      `${baseUrl}/api/v1/reactions/react`,
      {
        headers: headersDefault(),
        data: { id: challengeId, type: "club/models/subscriber-challenge", reaction: "like" },
      }
    );
    return { ...response.data, challengeId };
  }
);
